import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDom from 'react-dom';
import { useHistory } from 'react-router-dom';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';
import { useDispatch, useSelector } from 'react-redux';
import { APP_URL, USERSROLE, HexBinClassBreaks } from 'types';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import {
  queryFeatureLayerExtentStart,
  getHexBinClassBreakStatisticsStart,
  clearFeatureLayerLayerExtent,
  clearFeatureLayerLayerFeatures
} from 'redux/map/actions';
import { setForwardEnquiryExternalId, fetchEnquiryById } from 'redux/enquiries';
import {
  setForwardReferralExternalId,
  fetchReferralById
} from 'redux/referrals';
import { selectBaseMapTheme } from 'redux/app/selectors';
import { removeAlertByGroup } from 'redux/alerts/actions';
import { useComponentMount } from 'utilities/hooks.util';
import PopUp, {
  PopUpOutFields
} from 'modules/enquiryHistory/components/MapView/popup';
import ReferralPopUp, {
  PopUpOutFields as ReferralPopUpOutFields
} from 'modules/referralHistory/components/MapView/popup';
import Utils from 'utilities/helper.util';
import { findLayerById } from 'utilities/map-instance.util';
import { debugWarn } from 'utilities/basemap-debug.util';

const {
  polygonDarkSymbol,
  polygonSymbol,
  pointSymbol,
  pointDarkSymbol,
  shapeMinScale,
  modules
} = MapSettings;

const showFeatureLayer = (layerName: string, visible: boolean) => {
  // Handle all layers with the same ID (in case of duplicates)
  const { loadedMap } = window;
  if (loadedMap) {
    const allLayers = loadedMap.allLayers.items.filter(
      (layer: any) => layer.id === layerName
    );
    allLayers.forEach((layer: any) => {
      layer.visible = visible;
    });
  }
};

const setDarkModePolygon = (layerName: string, darkMode: boolean) => {
  // Use centralized utility to find layer
  var layer: any = findLayerById(layerName);
  if (layer) {
    layer.renderer = {
      type: 'simple',
      symbol: darkMode ? polygonDarkSymbol : polygonSymbol
    };
  }
};

const setDarkModePoint = (layerName: string, darkMode: boolean) => {
  // Use centralized utility to find layer
  var layer: any = findLayerById(layerName);
  if (layer) {
    layer.renderer = {
      type: 'simple',
      symbol: darkMode ? pointDarkSymbol : pointSymbol
    };
  }
};

const setRenderer = (
  layerName: string,
  baseMapTheme: any,
  field: string,
  breaks?: HexBinClassBreaks
) => {
  // Use centralized utility to find layer
  var layer: any = findLayerById(layerName);
  if (layer) {
    if (breaks) {
      var theme: 'dark' | 'light' = baseMapTheme === 'dark' ? 'dark' : 'light';
      var classBreakInfos = [
        {
          minValue: 0,
          maxValue: breaks.p20,
          color: {
            light: [208, 226, 255, 0.7],
            dark: [208, 226, 255, 0.8]
          }
        },
        {
          minValue: breaks.p20,
          maxValue: breaks.p40,
          color: {
            light: [166, 200, 255, 0.6],
            dark: [166, 200, 255, 0.75]
          }
        },
        {
          minValue: breaks.p40,
          maxValue: breaks.p60,
          color: {
            light: [69, 137, 255, 0.5],
            dark: [69, 137, 255, 0.65]
          }
        },
        {
          minValue: breaks.p60,
          maxValue: breaks.p80,
          color: {
            light: [0, 67, 206, 0.6],
            dark: [0, 67, 206, 0.65]
          }
        },
        {
          minValue: breaks.p80,
          maxValue: Number.MAX_SAFE_INTEGER,
          color: {
            light: [0, 45, 156, 0.7],
            dark: [0, 29, 108, 0.7]
          }
        }
      ].map(function (item, idx) {
        return {
          minValue: Math.floor(item.minValue) + 0.1 * idx,
          maxValue: Math.floor(item.maxValue) + 0.1 * idx,
          symbol: {
            type: 'simple-fill',
            color: item.color[theme],
            style: 'solid',
            outline: { width: 0, color: [0, 0, 0, 0] }
          }
        };
      });

      var renderer = {
        type: 'class-breaks',
        valueExpression: '$feature.' + field,
        classBreakInfos: classBreakInfos
      };

      var labelingInfo = [
        {
          minValue: 0,
          maxValue: breaks.p20,
          color: {
            light: [0, 67, 206],
            dark: [0, 67, 206]
          }
        },
        {
          minValue: breaks.p20,
          maxValue: breaks.p40,
          color: {
            light: [0, 45, 156],
            dark: [0, 45, 156]
          }
        },
        {
          minValue: breaks.p40,
          maxValue: breaks.p60,
          color: {
            light: [0, 29, 108],
            dark: [237, 245, 255]
          }
        },
        {
          minValue: breaks.p60,
          maxValue: breaks.p80,
          color: {
            light: [208, 226, 255],
            dark: [208, 226, 255]
          }
        },
        {
          minValue: breaks.p80,
          maxValue: Number.MAX_SAFE_INTEGER,
          color: {
            light: [166, 200, 255],
            dark: [166, 200, 255]
          }
        }
      ].map(function (item, idx) {
        return {
          labelExpressionInfo: {
            expression: '$feature.' + field
          },
          where:
            field +
            ' >= ' +
            (Math.floor(item.minValue) + 0.1 * idx) +
            ' AND ' +
            field +
            ' < ' +
            (Math.floor(item.maxValue) + 0.1 * (idx + 1)),
          minScale: 36500000,
          symbol: {
            type: 'text',
            color: item.color[theme],
            font: { family: 'sans-serif', size: 11, weight: 'bold' },
            yoffset: -3
          }
        };
      });

      layer.renderer = renderer;
      layer.labelingInfo = labelingInfo;
    } else {
      layer.renderer = {
        type: 'simple',
        symbol: {
          type: 'simple-fill',
          color: [0, 0, 0, 0],
          outline: { width: 0, color: [0, 0, 0, 0] }
        }
      };
    }
  }
};

const MapView: React.FC<Props> = ({
  layerExtent,
  userId,
  userRole,
  range,
  tenantId,
  organisationId,
  showAdminInfo,
  showGridMap,
  showShapeMap,
  showPointMap,
  startDate,
  endDate,
  mapSearchWithExtentZoom,
  hexbinClassBreaks
}) => {
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const extraMapModules = [modules.featureLayer];
  const [resolution, setResolution] = useState<number>(0);
  const [gridField, setGridField] = useState<string>('');
  const dispatch = useDispatch();
  const history = useHistory();
  const showShapeMapRef = useRef<boolean>(showShapeMap);
  const showPointMapRef = useRef<boolean>(showPointMap);
  const baseMapTheme = useSelector(selectBaseMapTheme);
  const mapSearchWithExtentZoomRef = useRef<boolean>(true);
  const showAdminInfoRef = useRef<boolean>(showAdminInfo);
  const scaleWatchHandleRef = useRef<any>(null);
  const isMountedRef = useRef<boolean>(true);

  // Track mounted state for async operations
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    showAdminInfoRef.current = showAdminInfo;
  }, [showAdminInfo]);

  const clickDetailButton = useCallback(
    (enquiry: any) => {
      const { loadedView } = window;
      // Safely close popup before navigation to prevent pending queries from causing errors
      if (loadedView && !loadedView.destroyed && loadedView.popup) {
        try {
          loadedView.closePopup();
        } catch (e) {
          debugWarn('Error closing popup:', e);
        }
      }
      dispatch(setForwardEnquiryExternalId(Number(enquiry.external_id)));
      dispatch(
        fetchEnquiryById({ enquiryId: Number(enquiry.objectid), logo: true })
      );
      // Small delay to allow popup to fully close before navigation
      // This prevents ArcGIS internal promise rejections when the view is destroyed
      setTimeout(() => {
        showAdminInfoRef.current
          ? history.push(APP_URL.ALL_ENQUIRIY_HISTORY)
          : history.push(APP_URL.ENQUIRIY_HISTORY);
      }, 50);
    },
    [dispatch, history]
  );

  const clickReferralDetailButton = useCallback(
    (referral: any) => {
      const { loadedView } = window;
      // Safely close popup before navigation to prevent pending queries from causing errors
      if (loadedView && !loadedView.destroyed && loadedView.popup) {
        try {
          loadedView.closePopup();
        } catch (e) {
          debugWarn('Error closing popup:', e);
        }
      }
      const {
        enquiry_id: enquiryId,
        objectid: referralId,
        external_id: externalId
      } = referral;
      const payload = {
        enquiryId,
        referralId
      };
      dispatch(setForwardReferralExternalId(Number(externalId)));
      dispatch(fetchReferralById(payload));
      // Small delay to allow popup to fully close before navigation
      // This prevents ArcGIS internal promise rejections when the view is destroyed
      setTimeout(() => {
        history.push(APP_URL.REFERRAL_HISTORY);
      }, 50);
    },
    [dispatch, history]
  );

  const addAssesOwnerLayers = useCallback(() => {
    const { loadedMapModules } = window;
    // Use centralized utility to find layer
    const layer = findLayerById('referrals-shape-layer');
    if (layer) {
      return;
    }
    if (loadedMapModules) {
      const referralShapeLayerUrl = getFeatureServerUrl('Referrals');
      const referralGridLayerUrl = getFeatureServerUrl('Referrals_hexbins');
      const { FeatureLayer } = loadedMapModules;
      const referalPopupTemplate = {
        content: (feature: any) => {
          const node = document.createElement('div');
          ReactDom.render(
            <ReferralPopUp
              attributes={feature?.graphic?.attributes || null}
              onButtonClick={clickReferralDetailButton}
            />,
            node
          );
          return node;
        },
        outFields: ReferralPopUpOutFields,
        overwriteActions: true
      };
      const referralShapeLayer = new FeatureLayer({
        id: 'referrals-shape-layer',
        url: referralShapeLayerUrl,
        outFields: ['objectid'],
        minScale: shapeMinScale,
        popupTemplate: referalPopupTemplate,
        renderer: {
          type: 'simple',
          symbol: baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol
        }
      });
      referralShapeLayer.visible = false;

      const referralGridLayer = new FeatureLayer({
        id: 'referrals-grid-layer',
        url: referralGridLayerUrl,
        outFields: ['objectid'],
        labelsVisible: true
      });
      referralGridLayer.visible = false;

      if (window.loadedMap) {
        window.loadedMap.addMany([referralShapeLayer, referralGridLayer]);
      }
    }
  }, [clickReferralDetailButton, baseMapTheme]);

  const addDBYDLayers = useCallback(() => {
    const { loadedMapModules } = window;
    // Use centralized utility to find layer
    const layer = findLayerById('all-enquiries-shape-layer');
    if (layer) {
      return;
    }
    if (loadedMapModules) {
      const shapeLayerUrl = getFeatureServerUrl('Enquiries');
      const gridLayerUrl = getFeatureServerUrl('Enquiries_hexbins');
      const { FeatureLayer } = loadedMapModules;
      const popupTemplate = {
        content: (feature: any) => {
          const node = document.createElement('div');
          ReactDom.render(
            <PopUp
              isFullSet={true}
              attributes={feature?.graphic?.attributes || null}
              geometry={feature?.graphic?.geometry}
              onButtonClick={clickDetailButton}
            />,
            node
          );
          return node;
        },
        outFields: PopUpOutFields,
        returnGeometry: true,
        overwriteActions: true
      };

      const shapeLayer = new FeatureLayer({
        id: 'all-enquiries-shape-layer',
        url: shapeLayerUrl,
        outFields: ['objectid'],
        minScale: shapeMinScale,
        popupTemplate,
        renderer: {
          type: 'simple',
          symbol: baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol
        }
      });
      shapeLayer.visible = false;

      const gridLayer = new FeatureLayer({
        id: 'all-enquiries-grid-layer',
        url: gridLayerUrl,
        outFields: ['objectid'],
        labelsVisible: true
      });
      gridLayer.visible = false;

      if (window.loadedMap) {
        window.loadedMap.addMany([shapeLayer, gridLayer]);
      }
    }
  }, [clickDetailButton, baseMapTheme]);

  const onMapReady = () => {
    const { loadedView, loadedMap, loadedMapModules } = window;
    if (loadedMap) {
      // Check if layers already exist to prevent duplicates
      const existingShapeLayer = findLayerById('enquiries-shape-layer');
      if (existingShapeLayer) {
        setIsMapLoaded(true);
        return;
      }

      const shapeLayerUrl = getFeatureServerUrl('Enquiries');
      const pointLayerUrl = getFeatureServerUrl('Enquiries_pts');
      const gridLayerUrl = getFeatureServerUrl('Enquiries_hexbins');
      const referralShapeLayerUrl = getFeatureServerUrl('Referrals');
      const referralGridLayerUrl = getFeatureServerUrl('Referrals_hexbins');

      const { Config, FeatureLayer } = loadedMapModules;
      Config.request.interceptors.push(
        {
          urls: shapeLayerUrl,
          before: featureRequestInterceptors
        },
        {
          urls: pointLayerUrl,
          before: featureRequestInterceptors
        },
        {
          urls: gridLayerUrl,
          before: featureRequestInterceptors
        },
        {
          urls: referralShapeLayerUrl,
          before: featureRequestInterceptors
        },
        {
          urls: referralGridLayerUrl,
          before: featureRequestInterceptors
        }
      );

      const popupTemplate = {
        content: (feature: any) => {
          const node = document.createElement('div');
          ReactDom.render(
            <PopUp
              attributes={feature?.graphic?.attributes || null}
              geometry={feature?.graphic?.geometry}
              onButtonClick={clickDetailButton}
            />,
            node
          );
          return node;
        },
        outFields: PopUpOutFields,
        returnGeometry: true,
        overwriteActions: true
      };

      const shapeLayer = new FeatureLayer({
        id: 'enquiries-shape-layer',
        url: shapeLayerUrl,
        outFields: ['objectid'],
        minScale: shapeMinScale,
        popupTemplate,
        renderer: {
          type: 'simple',
          symbol: baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol
        }
      });

      const pointLayer = new FeatureLayer({
        id: 'enquiries-point-layer',
        url: pointLayerUrl,
        outFields: ['objectid'],
        popupTemplate,
        renderer: {
          type: 'simple',
          symbol: baseMapTheme === 'dark' ? pointDarkSymbol : pointSymbol
        }
      });
      pointLayer.visible = false;

      // Apply the correct where clause immediately on layer creation so the
      // first request is filtered (avoids default 1=1 on initial load)
      if (userId && startDate && endDate && !showAdminInfo) {
        const initialDefinitionExpression = `user_id = ${userId} AND created_at >= TIMESTAMP '${Utils.getNewDate(
          startDate
        )
          .utc()
          .format(
            'YYYY-MM-DD HH:mm:ss'
          )}' AND created_at < TIMESTAMP '${Utils.getNewDate(endDate)
          .add(1, 'day')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')}' AND is_sandbox_test = false`;
        shapeLayer.definitionExpression = initialDefinitionExpression;
        pointLayer.definitionExpression = initialDefinitionExpression;
      }

      if (window.loadedMap) {
        window.loadedMap.addMany([shapeLayer, pointLayer]);
      }

      loadedView.popup.visibleElements = {
        featureNavigation: false,
        closeButton: false
      };
      loadedView.popup.actions = null;
      loadedView.popup.dockOptions = {
        buttonEnabled: false,
        position: 'bottom-center'
      };

      const calculatePopupAlignment = () => {
        let popupPos = 'auto';

        const popupRectBoundary = loadedView.popup.domNode.getBoundingClientRect();
        const finalPopupXPos = popupRectBoundary.x + popupRectBoundary.width;

        const popupY = loadedView.popup?.location?.y;
        const mapY = loadedView.center?.y;
        const popupX = loadedView.popup?.location?.x;
        const mapX = loadedView.center?.x;

        const minX: number = loadedView.position[0];
        const maxX: number = loadedView.size[0];

        if (popupY && mapY) {
          popupPos = popupY > mapY ? 'bottom-center' : 'top-center';
        }

        if (popupPos === 'bottom-center') {
          popupPos =
            popupX < mapX
              ? popupRectBoundary.x < minX
                ? 'bottom-right'
                : 'bottom-center'
              : finalPopupXPos > maxX
              ? 'bottom-left'
              : 'bottom-center';
        }

        if (popupPos === 'top-center') {
          popupPos =
            popupX < mapX
              ? popupRectBoundary.x < minX
                ? 'top-right'
                : 'top-center'
              : 'auto';
        }

        return popupPos;
      };

      loadedView.popup.alignment = () => calculatePopupAlignment();

      // Store the watch handle so we can clean it up on unmount
      // This prevents the "Cannot read properties of null (reading 'reject')" error
      // when the view is destroyed while the watcher is still active
      scaleWatchHandleRef.current = loadedView.watch(
        'scale',
        function (newValue: any) {
          // Check if component is still mounted before updating state
          if (!isMountedRef.current) return;
          if (newValue > 5000000) {
            return setResolution(3);
          } else if (newValue > 3000000) {
            return setResolution(4);
          } else if (newValue > 1000000) {
            return setResolution(5);
          } else if (newValue > 500000) {
            return setResolution(6);
          } else if (newValue > 120000) {
            return setResolution(7);
          } else {
            return setResolution(8);
          }
        }
      );
    }
    setIsMapLoaded(true);
  };

  const setShapeDefinitionExpression = useCallback(
    (
      userId: number,
      startDate: string,
      endDate: string,
      skipExtentQuery = false
    ) => {
      // Use centralized utility to find layers
      var shapeLayer: any = findLayerById('enquiries-shape-layer');
      var pointLayer: any = findLayerById('enquiries-point-layer');

      if (shapeLayer && pointLayer) {
        const definitionExpression = `user_id = ${userId} AND created_at >= TIMESTAMP '${Utils.getNewDate(
          startDate
        )
          .utc()
          .format(
            'YYYY-MM-DD HH:mm:ss'
          )}' AND created_at < TIMESTAMP '${Utils.getNewDate(endDate)
          .add(1, 'day')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')}' AND is_sandbox_test = false`;

        // Check if the definition expression is already set correctly
        const isAlreadySet =
          shapeLayer.definitionExpression === definitionExpression &&
          pointLayer.definitionExpression === definitionExpression;

        if (!isAlreadySet) {
          shapeLayer.definitionExpression = definitionExpression;
          pointLayer.definitionExpression = definitionExpression;

          // Only query extent if not skipping and expression was actually updated
          if (!skipExtentQuery) {
            dispatch(queryFeatureLayerExtentStart(shapeLayer));
          }
        }
      }
    },
    [dispatch]
  );

  const setDBYDShapeDefinitionExpression = useCallback(
    (startDate: string, endDate: string, tenantId?: number) => {
      // Use centralized utility to find layer
      var shapeLayer: any = findLayerById('all-enquiries-shape-layer');

      if (shapeLayer) {
        const definitionExpression = `created_at >= TIMESTAMP '${Utils.getNewDate(
          startDate
        )
          .utc()
          .format(
            'YYYY-MM-DD HH:mm:ss'
          )}' AND created_at < TIMESTAMP '${Utils.getNewDate(endDate)
          .add(1, 'day')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')}' AND is_sandbox_test = false${
          tenantId ? ` AND tenant_id = ${tenantId}` : ''
        }`;
        if (shapeLayer) {
          shapeLayer.definitionExpression = definitionExpression;
          dispatch(queryFeatureLayerExtentStart(shapeLayer));
        }
      }
    },
    [dispatch]
  );

  const setRefferalDefinitionExpression = useCallback(
    (organisationId: number, startDate: string, endDate: string) => {
      // Use centralized utility to find layer
      var shapeLayer: any = findLayerById('referrals-shape-layer');

      if (shapeLayer) {
        const definitionExpression = `organisation_id = ${organisationId} AND created_at >= TIMESTAMP '${Utils.getNewDate(
          startDate
        )
          .utc()
          .format(
            'YYYY-MM-DD HH:mm:ss'
          )}' AND created_at < TIMESTAMP '${Utils.getNewDate(endDate)
          .add(1, 'day')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')}' AND is_sandbox_test = false`;
        if (shapeLayer) {
          shapeLayer.definitionExpression = definitionExpression;
          dispatch(queryFeatureLayerExtentStart(shapeLayer));
        }
      }
    },
    [dispatch]
  );

  const setGridQuery = useCallback(
    (
      userRole: string,
      id: number | undefined,
      resolution: number,
      onStatisticField: string
    ) => {
      // Use centralized utility to find layer
      var gridLayer: any = findLayerById(
        userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER
          ? 'referrals-grid-layer'
          : 'all-enquiries-grid-layer'
      );

      if (gridLayer) {
        const idFilterString =
          userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER
            ? 'organisation_id = ' + id + ' AND '
            : id
            ? 'tenant_id = ' + id + ' AND '
            : '';
        var definitionExpression =
          idFilterString +
          'h3_rslt = ' +
          resolution +
          ' AND ' +
          onStatisticField +
          ' > 0';
        gridLayer.definitionExpression = definitionExpression;
        dispatch(
          getHexBinClassBreakStatisticsStart(
            gridLayer,
            resolution,
            onStatisticField
          )
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    setRenderer(
      userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER
        ? 'referrals-grid-layer'
        : 'all-enquiries-grid-layer',
      baseMapTheme,
      gridField,
      hexbinClassBreaks
    );
  }, [hexbinClassBreaks, gridField, userRole, baseMapTheme]);

  useEffect(() => {
    const shouldShowShapeLayer =
      isMapLoaded &&
      showShapeMap &&
      userId &&
      startDate &&
      endDate &&
      !showAdminInfo;

    if (shouldShowShapeLayer && userId && startDate && endDate) {
      // Force set the definition expression before showing the layer
      // Skip extent query to avoid zooming when toggling visibility
      setShapeDefinitionExpression(userId, startDate, endDate, true);
      showFeatureLayer('enquiries-shape-layer', true);
    } else if (isMapLoaded && !showShapeMap) {
      showFeatureLayer('enquiries-shape-layer', false);
    }
    showShapeMapRef.current = showShapeMap;
  }, [
    isMapLoaded,
    showShapeMap,
    userId,
    startDate,
    endDate,
    showAdminInfo,
    setShapeDefinitionExpression
  ]);

  useEffect(() => {
    // Only show the point layer after the definitionExpression has been set
    // to avoid sending a query with the default 1=1 where clause
    const shouldShowPointLayer =
      isMapLoaded &&
      showPointMap &&
      userId &&
      startDate &&
      endDate &&
      !showAdminInfo;

    if (shouldShowPointLayer && userId && startDate && endDate) {
      // Force set the definition expression before showing the layer
      // to ensure it's always up-to-date, especially after navigation
      // Skip extent query to avoid zooming when toggling visibility
      setShapeDefinitionExpression(userId, startDate, endDate, true);
      showFeatureLayer('enquiries-point-layer', true);
    } else if (isMapLoaded && !showPointMap) {
      showFeatureLayer('enquiries-point-layer', false);
    }
    showPointMapRef.current = showPointMap;
  }, [
    isMapLoaded,
    showPointMap,
    userId,
    startDate,
    endDate,
    showAdminInfo,
    setShapeDefinitionExpression
  ]);

  useEffect(() => {
    if (
      (userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER) &&
      isMapLoaded
    ) {
      if (showAdminInfo) {
        showFeatureLayer('referrals-shape-layer', !showGridMap);
        showFeatureLayer('referrals-grid-layer', showGridMap);
        showFeatureLayer('enquiries-point-layer', false);
        showFeatureLayer('enquiries-shape-layer', false);
      } else {
        showFeatureLayer('referrals-shape-layer', false);
        showFeatureLayer('referrals-grid-layer', false);
        showFeatureLayer('enquiries-point-layer', showPointMapRef.current);
        showFeatureLayer('enquiries-shape-layer', showShapeMapRef.current);
      }
    }

    if (
      (userRole === USERSROLE.DBYDADMIN || userRole === USERSROLE.SYSADMIN) &&
      isMapLoaded
    ) {
      if (showAdminInfo) {
        showFeatureLayer('all-enquiries-shape-layer', !showGridMap);
        showFeatureLayer('all-enquiries-grid-layer', showGridMap);
        showFeatureLayer('enquiries-point-layer', false);
        showFeatureLayer('enquiries-shape-layer', false);
      } else {
        showFeatureLayer('all-enquiries-shape-layer', false);
        showFeatureLayer('all-enquiries-grid-layer', false);
        showFeatureLayer('enquiries-point-layer', showPointMapRef.current);
        showFeatureLayer('enquiries-shape-layer', showShapeMapRef.current);
      }
    }
  }, [isMapLoaded, userRole, showAdminInfo, showGridMap]);

  useEffect(() => {
    if (isMapLoaded) {
      const isDarkMode = baseMapTheme === 'dark';
      setDarkModePolygon('enquiries-shape-layer', isDarkMode);
      setDarkModePolygon('all-enquiries-shape-layer', isDarkMode);
      setDarkModePolygon('referrals-shape-layer', isDarkMode);
      setDarkModePoint('enquiries-point-layer', isDarkMode);
    }
  }, [baseMapTheme, isMapLoaded]);

  useEffect(() => {
    if (layerExtent) {
      if (typeof layerExtent !== 'string') {
        if (!layerExtent?.noRecord && mapSearchWithExtentZoomRef.current) {
          zoomToFeature(layerExtent);
          window.mapOriginalExtent = layerExtent;
        }
      }
    }
  }, [layerExtent, dispatch]);

  useEffect(() => {
    mapSearchWithExtentZoomRef.current = mapSearchWithExtentZoom[0];

    if (userId && isMapLoaded && startDate && endDate && !showAdminInfo) {
      setShapeDefinitionExpression(userId, startDate, endDate);
    }
  }, [
    userId,
    isMapLoaded,
    startDate,
    endDate,
    showAdminInfo,
    setShapeDefinitionExpression,
    mapSearchWithExtentZoom
  ]);

  useEffect(() => {
    (userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER) &&
      isMapLoaded &&
      addAssesOwnerLayers();
    (userRole === USERSROLE.DBYDADMIN || userRole === USERSROLE.SYSADMIN) &&
      isMapLoaded &&
      addDBYDLayers();
  }, [userRole, isMapLoaded, addAssesOwnerLayers, addDBYDLayers]);

  useEffect(() => {
    mapSearchWithExtentZoomRef.current = mapSearchWithExtentZoom[0];

    organisationId &&
      isMapLoaded &&
      startDate &&
      endDate &&
      showAdminInfo &&
      (userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER) &&
      setRefferalDefinitionExpression(organisationId, startDate, endDate);
  }, [
    organisationId,
    userRole,
    isMapLoaded,
    startDate,
    endDate,
    showAdminInfo,
    setRefferalDefinitionExpression,
    mapSearchWithExtentZoom
  ]);

  useEffect(() => {
    mapSearchWithExtentZoomRef.current = mapSearchWithExtentZoom[0];

    isMapLoaded &&
      startDate &&
      endDate &&
      showAdminInfo &&
      (userRole === USERSROLE.DBYDADMIN || userRole === USERSROLE.SYSADMIN) &&
      setDBYDShapeDefinitionExpression(startDate, endDate, tenantId);
  }, [
    userRole,
    isMapLoaded,
    startDate,
    endDate,
    tenantId,
    showAdminInfo,
    setDBYDShapeDefinitionExpression,
    mapSearchWithExtentZoom
  ]);

  useEffect(() => {
    if (
      organisationId &&
      (userRole === USERSROLE.AOADMIN || userRole === USERSROLE.AOVIEWER) &&
      isMapLoaded &&
      gridField &&
      showAdminInfo &&
      resolution > 0
    ) {
      const expectedId = 'referrals-grid-layer';
      const existing = findLayerById(expectedId);
      if (!existing) {
        addAssesOwnerLayers();
        setTimeout(() => {
          const recheck = findLayerById(expectedId);
          if (recheck) {
            setGridQuery(userRole, organisationId, resolution, gridField);
          }
        }, 250);
        return;
      }
      setGridQuery(userRole, organisationId, resolution, gridField);
    }
  }, [
    organisationId,
    userRole,
    isMapLoaded,
    gridField,
    resolution,
    showAdminInfo,
    setGridQuery,
    addAssesOwnerLayers
  ]);

  useEffect(() => {
    if (
      (userRole === USERSROLE.DBYDADMIN || userRole === USERSROLE.SYSADMIN) &&
      isMapLoaded &&
      gridField &&
      showAdminInfo &&
      resolution > 0
    ) {
      const expectedId = 'all-enquiries-grid-layer';
      const existing = findLayerById(expectedId);
      if (!existing) {
        addDBYDLayers();
        setTimeout(() => {
          const recheck = findLayerById(expectedId);
          if (recheck) {
            setGridQuery(userRole, tenantId, resolution, gridField);
          }
        }, 250);
        return;
      }
      setGridQuery(userRole, tenantId, resolution, gridField);
    }
  }, [
    tenantId,
    userRole,
    isMapLoaded,
    gridField,
    resolution,
    showAdminInfo,
    setGridQuery,
    addDBYDLayers
  ]);

  useEffect(() => {
    if (range) {
      switch (range) {
        case 'this-week':
          return setGridField('this_week');
        case 'last-7-days': {
          return setGridField('last_7_days');
        }
        case 'last-30-days': {
          return setGridField('last_30_days');
        }
        default: {
          return setGridField('today');
        }
      }
    }
  }, [range]);

  useComponentMount(() => {
    return () => {
      // Clean up the scale watcher to prevent errors when the view is destroyed
      if (scaleWatchHandleRef.current) {
        try {
          scaleWatchHandleRef.current.remove();
        } catch (e) {
          debugWarn('Error removing scale watcher:', e);
        }
        scaleWatchHandleRef.current = null;
      }

      // Close popup before unmounting to prevent pending feature queries
      // from causing errors when they resolve after the view is destroyed
      const { loadedView } = window;
      if (loadedView && !loadedView.destroyed && loadedView.popup) {
        try {
          loadedView.popup.close();
        } catch (e) {
          debugWarn('Error closing popup on unmount:', e);
        }
      }

      dispatch(removeAlertByGroup('map-alert'));
      dispatch(clearFeatureLayerLayerExtent());
      dispatch(clearFeatureLayerLayerFeatures());
    };
  });

  return (
    <div className="dashboard-component--map">
      <Map
        showHomeButton={true}
        extraModules={extraMapModules}
        onReady={onMapReady}
        showbaseMapButton={true}
      />
    </div>
  );
};

type Props = {
  layerExtent: any;
  userId?: number;
  userRole?: string;
  range: string;
  organisationId?: number;
  tenantId?: number;
  showAdminInfo: boolean;
  showGridMap: boolean;
  showShapeMap: boolean;
  showPointMap: boolean;
  startDate: string;
  endDate: string;
  mapSearchWithExtentZoom: boolean[];
  hexbinClassBreaks?: HexBinClassBreaks;
};

export default React.memo(MapView);
